/**
 * Injects Versicherungsvertrag file uploads into ImpowerX insurance sub-sections when the
 * corresponding Ja/Nein radio is Ja. Uses the same .FileUpload + processFileUploads flow as
 * native Dienstleister contract fields.
 */
;(function () {
  const SLOT_ATTR = 'data-impowerx-vv-slot'
  const COPY_LISTENER_ATTR = 'data-impowerx-insurance-vv-copy-bound'

  function rowRadioIsYes(row) {
    const selected = row.querySelector('.ant-radio-wrapper-checked .ant-radio-input')
    return !!(selected && selected.value === 'true')
  }

  function isBinaryTrueFalseRow(row) {
    const inputs = row.querySelectorAll('.ant-radio-input')
    if (inputs.length !== 2) return false
    const vals = Array.from(new Set(Array.from(inputs).map((i) => i.value)))
    return vals.includes('true') && vals.includes('false')
  }

  function findRowByLabelContains(section, text) {
    const rows = section.querySelectorAll(':scope > .sub-section-content > .sub-section-content-row')
    for (const row of rows) {
      const labelEl = row.querySelector('.ant-form-item-label label')
      const title = labelEl?.getAttribute('title') || ''
      const tx = labelEl?.textContent || ''
      if (title.includes(text) || tx.includes(text)) return row
    }
    return null
  }

  function findBuildingToggleRow(section) {
    const rows = section.querySelectorAll(':scope > .sub-section-content > .sub-section-content-row')
    for (const row of rows) {
      if (isBinaryTrueFalseRow(row)) return row
    }
    return null
  }

  function ensureDataBuildingId(subSection, buildings) {
    const m = subSection.id && subSection.id.match(/^insurance-building-(\d+)$/)
    if (!m || !buildings || !buildings.length) return
    if (subSection.getAttribute('data-building-id')) return
    const idx = parseInt(m[1], 10) - 1
    if (idx >= 0 && buildings[idx] && buildings[idx].id != null) {
      subSection.setAttribute('data-building-id', String(buildings[idx].id))
    }
  }

  /** Follow insuranceLinkedBuildingHrId to the root building (same idea as heating). */
  function resolveInsuranceRootBuilding(building, byHrId) {
    let current = building
    const visited = new Set()
    while (current?.insuranceLinkedBuildingHrId) {
      const key = current.buildingHrId || String(current.id ?? '')
      if (!key || visited.has(key)) {
        console.warn('[ImpowerX] Circular insurance link detected:', key)
        break
      }
      visited.add(key)
      const next = byHrId.get(String(current.insuranceLinkedBuildingHrId))
      if (!next) break
      current = next
    }
    return current
  }

  function getInsuranceSourceBuildingId(subSection, buildings) {
    if (!Array.isArray(buildings) || buildings.length === 0) return null
    const byHrId = new Map()
    const byId = new Map()
    buildings.forEach((b) => {
      if (!b || b.id == null) return
      byId.set(String(b.id), b)
      if (b.buildingHrId) byHrId.set(String(b.buildingHrId), b)
    })
    const currentId = subSection.getAttribute('data-building-id')
    let current = currentId ? byId.get(String(currentId)) : null
    if (!current) return currentId || null
    current = resolveInsuranceRootBuilding(current, byHrId)
    return current?.id != null ? String(current.id) : currentId
  }

  function removeExistingSlot(container, slotKey) {
    const el = container.querySelector(`[${SLOT_ATTR}="${slotKey}"]`)
    if (el) el.remove()
  }

  function isInsuranceCopyCheckboxDisabled(wrapper) {
    if (!wrapper) return true
    if (wrapper.classList.contains('ant-checkbox-disabled')) return true
    const inp = wrapper.querySelector('input.ant-checkbox-input')
    return !!(inp && inp.disabled)
  }

  function setInsuranceInheritedColumnAttr(col, fileInputId, opts) {
    if (!opts || String(opts.sourceBuildingId) === String(opts.bid)) return
    const showingSource =
      fileInputId === opts.inheritedFileInputId && String(opts.sourceBuildingId) !== String(opts.bid)
    col.setAttribute(
      'data-inherited-building-id',
      showingSource ? String(opts.sourceBuildingId) : String(opts.bid)
    )
  }

  /**
   * After processFileUploads replaces the inner control, the outer .impower-x from buildUploadRow
   * can still carry disabled; nested wrappers from generateFileInput may too. Document list rows
   * (delete buttons) render asynchronously. Use native DOM APIs (not jQuery removeAttr).
   */
  function enableInsuranceUploadRowControls(row) {
    if (!row) return
    row.querySelectorAll('.impower-x').forEach((el) => {
      el.removeAttribute('disabled')
    })
    const fileInput = row.querySelector('input[type="file"]')
    if (fileInput) {
      fileInput.removeAttribute('disabled')
      fileInput.disabled = false
    }
    const uploadBtn =
      row.querySelector('button.file-input-label') || row.querySelector('.FileUpload button[type="button"]')
    if (uploadBtn) {
      uploadBtn.removeAttribute('disabled')
      uploadBtn.disabled = false
      uploadBtn.classList.remove('disabled', 'ant-btn-disabled')
    }
    row.querySelectorAll('.delete-button').forEach((btn) => {
      btn.removeAttribute('disabled')
      btn.disabled = false
      btn.classList.remove('disabled', 'ant-btn-disabled')
    })
  }

  /**
   * One listener per copy checkbox: remove slot row and rebuild with inherited or local file input id.
   * Replacing the DOM row lets processFileUploads run on a fresh .FileUpload (WeakSet).
   */
  function bindInsuranceCopyCheckboxOnce(
    section,
    content,
    slotKey,
    inheritanceOpts,
    shouldDisable,
    processFileUploads,
    insertAfterRow
  ) {
    const copyWrapper = section.querySelector('.sub-section-header .CheckboxInput .ant-checkbox-wrapper')
    if (!copyWrapper || copyWrapper.getAttribute(COPY_LISTENER_ATTR)) return

    copyWrapper.setAttribute(COPY_LISTENER_ATTR, '1')
    copyWrapper.addEventListener('click', () => {
      setTimeout(() => {
        if (isInsuranceCopyCheckboxDisabled(copyWrapper)) return

        const checked = copyWrapper.classList.contains('ant-checkbox-wrapper-checked')
        const fileInputId = checked
          ? inheritanceOpts.inheritedFileInputId
          : inheritanceOpts.localFileInputId

        removeExistingSlot(content, slotKey)
        const row = buildUploadRow(slotKey, fileInputId, shouldDisable, inheritanceOpts)
        if (insertAfterRow && insertAfterRow.parentNode === content) {
          insertAfterRow.after(row)
        } else {
          content.appendChild(row)
        }
        void processFileUploads(section).then(() => {
          enableInsuranceUploadRowControls(row)
          setTimeout(() => enableInsuranceUploadRowControls(row), 200)
        })
      }, 0)
    })
  }

  function buildUploadRow(slotKey, fileInputId, shouldDisable, inheritanceOptions) {
    const row = document.createElement('div')
    row.className = 'sub-section-content-row'
    row.setAttribute(SLOT_ATTR, slotKey)

    const col = document.createElement('div')
    col.className = 'sub-section-content-column'
    col.style.minWidth = '25rem'

    const fu = document.createElement('div')
    fu.className = 'FileUpload'

    const firstRow = document.createElement('div')
    firstRow.className = 'first-row'

    const label = document.createElement('label')
    label.className = 'description-label'
    label.setAttribute('for', fileInputId)
    label.textContent = 'Versicherungsvertrag (optional)'

    const wrap = document.createElement('div')
    wrap.className = 'impower-x'
    if (shouldDisable) wrap.setAttribute('disabled', 'true')

    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'ant-btn ant-btn-default Button file-input-label'
    if (shouldDisable) btn.setAttribute('disabled', '')
    btn.innerHTML =
      '<span role="img" class="anticon"><svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 4.41009V15.0001H10V4.41009L13.29 7.71009L14.71 6.29009L9 0.590088L3.29 6.29009L4.71 7.71009L8 4.41009ZM0 18.0001V20.0001H18V18.0001H0Z" fill="black"></path></svg></span><span>Dateien auswählen</span>'

    const input = document.createElement('input')
    input.id = fileInputId
    input.className = 'file-input'
    input.type = 'file'
    input.accept = '.pdf,.doc,.xlsx,.jpg,.png'
    input.multiple = true
    if (shouldDisable) input.setAttribute('disabled', '')

    if (inheritanceOptions && String(inheritanceOptions.sourceBuildingId) !== String(inheritanceOptions.bid)) {
      setInsuranceInheritedColumnAttr(col, fileInputId, inheritanceOptions)
    }

    wrap.appendChild(btn)
    wrap.appendChild(input)
    firstRow.appendChild(label)
    firstRow.appendChild(wrap)
    fu.appendChild(firstRow)
    col.appendChild(fu)
    row.appendChild(col)
    return row
  }

  function syncIdentitySection(shouldDisable, processFileUploads) {
    const section = document.getElementById('insuranceIdentity')
    if (!section) return
    const content = section.querySelector('.sub-section-content')
    if (!content) return
    const row = findRowByLabelContains(section, 'Versicherungen identisch')
    const slotKey = 'impowerx-vv-identity'
    removeExistingSlot(content, slotKey)
    if (!row || !rowRadioIsYes(row)) return
    const fileInputId = 'impowerx-insuranceidentityversicherungsvertrag-file-input'
    row.after(buildUploadRow(slotKey, fileInputId, shouldDisable))
    processFileUploads(section)
  }

  function syncWegSection(shouldDisable, processFileUploads) {
    const section = document.getElementById('insurance-weg')
    if (!section) return
    const content = section.querySelector('.sub-section-content')
    if (!content) return

    const beiratRow = findRowByLabelContains(section, 'Beiratsversicherung')
    const slotBeirat = 'impowerx-vv-weg-beirat'
    removeExistingSlot(content, slotBeirat)
    if (beiratRow && rowRadioIsYes(beiratRow)) {
      const id = 'impowerx-insurancewegbeiratversicherungsvertrag-file-input'
      beiratRow.after(buildUploadRow(slotBeirat, id, shouldDisable))
    }

    const hghRow = findRowByLabelContains(section, 'Haus- und Grundbesitzer Haftpflicht')
    const slotHgh = 'impowerx-vv-weg-hgh'
    removeExistingSlot(content, slotHgh)
    if (hghRow && rowRadioIsYes(hghRow)) {
      const id = 'impowerx-insuranceweghausgrhaftversicherungsvertrag-file-input'
      hghRow.after(buildUploadRow(slotHgh, id, shouldDisable))
    }

    processFileUploads(section)
  }

  function syncBuildingSections(shouldDisable, processFileUploads, buildings) {
    document.querySelectorAll('[id^="insurance-building-"]').forEach((section) => {
      const content = section.querySelector('.sub-section-content')
      if (!content) return
      const m = section.id.match(/^insurance-building-(\d+)$/)
      const slotKey = `impowerx-vv-building-${m ? m[1] : '0'}`
      removeExistingSlot(content, slotKey)
      ensureDataBuildingId(section, buildings)
      const toggleRow = findBuildingToggleRow(section)
      if (!toggleRow || !rowRadioIsYes(toggleRow)) return

      const bid = section.getAttribute('data-building-id') || '0'
      const sourceBuildingId = getInsuranceSourceBuildingId(section, buildings) || bid
      const localFileInputId = `impowerx-insurancebuildingversicherungsvertrag-${bid}-file-input`
      const inheritedFileInputId = `impowerx-insurancebuildingversicherungsvertrag-${sourceBuildingId}-file-input`

      if (String(sourceBuildingId) === String(bid)) {
        toggleRow.after(buildUploadRow(slotKey, localFileInputId, shouldDisable))
        colSetInheritedForStandalone(content, slotKey, bid)
        processFileUploads(section)
        return
      }

      const inheritanceOpts = {
        bid,
        sourceBuildingId,
        localFileInputId,
        inheritedFileInputId,
      }

      toggleRow.after(buildUploadRow(slotKey, inheritedFileInputId, shouldDisable, inheritanceOpts))
      bindInsuranceCopyCheckboxOnce(
        section,
        content,
        slotKey,
        inheritanceOpts,
        shouldDisable,
        processFileUploads,
        toggleRow
      )
      processFileUploads(section)
    })
  }

  function colSetInheritedForStandalone(content, slotKey, bid) {
    const row = content.querySelector(`[${SLOT_ATTR}="${slotKey}"]`)
    const col = row?.querySelector('.sub-section-content-column')
    if (col) col.setAttribute('data-inherited-building-id', String(bid))
  }

  /**
   * @param {{ processFileUploads: function(Element): void, buildings?: Array<{ id?: string|number }>, shouldDisableInjectedFields?: function(): boolean }} ctx
   */
  window.runInsuranceVersicherungsvertragInjection = function (ctx) {
    if (!ctx || typeof ctx.processFileUploads !== 'function') return
    const disabled =
      typeof ctx.shouldDisableInjectedFields === 'function' ? ctx.shouldDisableInjectedFields() : false
    try {
      syncIdentitySection(disabled, ctx.processFileUploads)
      syncWegSection(disabled, ctx.processFileUploads)
      syncBuildingSections(disabled, ctx.processFileUploads, ctx.buildings)
    } catch (e) {
      console.error('[ImpowerX] Insurance Versicherungsvertrag injection failed', e)
    }
  }
})()
